// Utility functions for location filtering and state name/abbreviation matching

// State abbreviation mapping
const STATE_ABBREVIATIONS: Record<string, string> = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
  'DC': 'District of Columbia'
};

// Reverse mapping for full state names to abbreviations
const STATE_NAMES_TO_ABBREV: Record<string, string> = Object.entries(STATE_ABBREVIATIONS)
  .reduce((acc, [abbrev, name]) => {
    acc[name.toLowerCase()] = abbrev;
    return acc;
  }, {} as Record<string, string>);

/**
 * Checks if a location string matches venue/show location data
 * Supports city names, state names, and state abbreviations (case-insensitive)
 */
export function matchesLocation(
  searchLocation: string,
  venueCity?: string | null,
  venueState?: string | null,
  marketName?: string | null
): boolean {
  if (!searchLocation || !searchLocation.trim()) return true;
  
  const searchLower = searchLocation.toLowerCase().trim();
  const searchTerms = searchLocation.split(',').map(t => t.trim().toLowerCase()).filter(t => t);
  
  // Helper function to normalize state for comparison
  const normalizeState = (state: string): string[] => {
    const stateLower = state.toLowerCase();
    const variations = [stateLower];
    
    // If it's an abbreviation, add the full name
    if (STATE_ABBREVIATIONS[state.toUpperCase()]) {
      variations.push(STATE_ABBREVIATIONS[state.toUpperCase()].toLowerCase());
    }
    
    // If it's a full name, add the abbreviation
    if (STATE_NAMES_TO_ABBREV[stateLower]) {
      variations.push(STATE_NAMES_TO_ABBREV[stateLower].toLowerCase());
    }
    
    return variations;
  };
  
  // Get normalized location data
  const cityLower = venueCity?.toLowerCase() || '';
  const stateVariations = venueState ? normalizeState(venueState) : [];
  const marketLower = marketName?.toLowerCase() || '';
  
  // Check if any search term matches
  return searchTerms.some(term => {
    // Check city match (only if term or city is substantial enough to avoid false matches)
    if (term.length >= 3 && cityLower.length >= 3) {
      if (cityLower.includes(term) || term.includes(cityLower)) return true;
    } else if (term.length >= 3 && cityLower === term) {
      return true;
    } else if (cityLower.length >= 3 && term === cityLower) {
      return true;
    }
    
    // Check state match - more precise matching for states
    if (stateVariations.length > 0) {
      // Exact match for state abbreviations (2 chars) or full state names
      if (stateVariations.includes(term)) return true;
    }
    
    // Direct state abbreviation/name matching
    const termUpper = term.toUpperCase();
    if (STATE_ABBREVIATIONS[termUpper] && venueState) {
      // Search term is a state abbreviation - exact match with venue state
      return venueState.toUpperCase() === termUpper || 
             venueState.toLowerCase() === STATE_ABBREVIATIONS[termUpper].toLowerCase();
    }
    
    // Check if search term is a full state name
    if (STATE_NAMES_TO_ABBREV[term.toLowerCase()] && venueState) {
      const stateAbbrev = STATE_NAMES_TO_ABBREV[term.toLowerCase()];
      return venueState.toUpperCase() === stateAbbrev || 
             venueState.toLowerCase() === term.toLowerCase();
    }
    
    // Check market name match (only for substantial terms to avoid false matches)
    if (marketLower && term.length >= 3 && marketLower.length >= 3) {
      if (marketLower.includes(term) || term.includes(marketLower)) return true;
    }
    
    return false;
  });
}

/**
 * Checks if location matches specifically for venue filtering
 */
export function venueMatchesLocation(
  searchLocation: string,
  venue: { city?: string | null; state?: string | null }
): boolean {
  return matchesLocation(searchLocation, venue.city, venue.state);
}

/**
 * Checks if location matches for show filtering (venue location and market)
 */
export function showMatchesLocation(
  searchLocation: string,
  show: { venue: { city?: string | null; state?: string | null; market?: { name?: string | null } | null } }
): boolean {
  return matchesLocation(searchLocation, show.venue.city, show.venue.state, show.venue.market?.name);
}

/**
 * Checks if location matches for market filtering (market name and venue locations)
 */
export function marketMatchesLocation(
  searchLocation: string,
  market: { 
    name: string;
    states?: string[] | null;
    shows: Array<{ venue: { city?: string | null; state?: string | null } }>
  }
): boolean {
  // Check market name first
  if (matchesLocation(searchLocation, null, null, market.name)) return true;
  
  // Check if any state in the market matches
  if (market.states && market.states.length > 0) {
    if (market.states.some(state => 
      matchesLocation(searchLocation, null, state)
    )) return true;
  }
  
  // Check if any venue in the market matches
  return market.shows.some(show => 
    matchesLocation(searchLocation, show.venue.city, show.venue.state)
  );
}